import { keyframes } from "@emotion/react";

export interface LogoMarkProps {
  /** Rendered width/height in px - the icon is square. */
  size?: number;
  /** Plays a looping "assembling" pulse on the three facets - use for loading states only. */
  animated?: boolean;
  className?: string;
}

const facetPulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1); }
`;

/**
 * Haystack Robotics' icon mark only - the wordmark portion of the source
 * artwork is deliberately excluded, cropped to just the isometric hexagon
 * (see the original at `Logo.svg`, group id="Logo", the nested <g> before
 * the letterform paths). Colors are literal brand values, not
 * theme-dependent, since they read fine on both light and dark backgrounds.
 */
export function LogoMark({ size = 40, animated = false, className }: LogoMarkProps) {
  const facetSx = animated
    ? {
        animation: `${facetPulse} 1.8s ease-in-out infinite`,
        transformBox: "fill-box" as const,
        transformOrigin: "center" as const,
      }
    : undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox="155 160 330 370"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="V&V logo"
    >
      <path
        fill="#3F3834"
        d="M319.36,523.61l-150.23-86.73c-1.96-1.13-3.18-3.23-3.18-5.5V257.91c0-2.27,1.21-4.37,3.18-5.5l150.23-86.73
          c1.96-1.13,4.39-1.13,6.35,0l150.23,86.73c1.96,1.13,3.18,3.23,3.18,5.5v173.47c0,2.27-1.21,4.37-3.18,5.5l-150.23,86.73
          C323.75,524.74,321.33,524.74,319.36,523.61z M192.46,419.74l130.08,75.1l130.08-75.1v-150.2l-130.08-75.1l-130.08,75.1V419.74z"
      />
      <g style={animated ? { ...facetSx, animationDelay: "0.6s" } : undefined}>
        <polygon
          fill="#5386A3"
          points="414.57,291.5 322.54,238.37 230.5,291.5 230.5,397.78 322.54,450.92 414.57,397.78"
        />
        <path
          fill="#3F3834"
          d="M322.54,457.85l-98.04-56.6v-113.2l98.04-56.6l98.04,56.6v113.2L322.54,457.85z M236.5,394.32l86.04,49.67
            l86.04-49.67v-99.35l-86.04-49.67l-86.04,49.67V394.32z"
        />
      </g>
      <rect
        fill="#3F3834"
        x="175.24"
        y="409.59"
        transform="matrix(0.866 -0.5001 0.5001 0.866 -178.8659 157.7298)"
        width="59.22"
        height="6"
      />
      <rect
        fill="#3F3834"
        x="437.22"
        y="382.97"
        transform="matrix(0.5 -0.866 0.866 0.5 -137.2038 587.5257)"
        width="6"
        height="59.22"
      />
      <rect fill="#3F3834" x="319.54" y="179.14" width="6" height="59.22" />
      <g style={animated ? { ...facetSx, animationDelay: "0s" } : undefined}>
        <polygon fill="#9D9C9E" points="230.5,291.5 322.54,238.64 414.57,291.5 322.54,344.64" />
        <path
          fill="#3F3834"
          d="M322.54,351.57l-104.06-60.08l104.06-59.78l104.06,59.78L322.54,351.57z M242.52,291.52l80.01,46.2
            l80.01-46.2l-80.01-45.96L242.52,291.52z"
        />
      </g>
      <g style={animated ? { ...facetSx, animationDelay: "1.2s" } : undefined}>
        <polygon fill="#99D6F9" points="414.64,291.5 414.41,397.65 322.61,450.92 322.61,344.64" />
        <path
          fill="#3F3834"
          d="M316.61,461.34l0-120.16l104.06-60.08l-0.26,120.01L316.61,461.34z M328.61,348.11l0,92.39l79.81-46.31
            l0.2-92.28L328.61,348.11z"
        />
      </g>
      <rect
        fill="#3F3834"
        x="244.87"
        y="220.52"
        transform="matrix(0.5 -0.866 0.866 0.5 -137.2014 368.8945)"
        width="12"
        height="165.5"
      />
      <rect
        fill="#3F3834"
        x="311.45"
        y="297.27"
        transform="matrix(0.866 -0.5 0.5 0.866 -98.8207 237.7239)"
        width="165.5"
        height="12"
      />
      <rect fill="#3F3834" x="316.54" y="344.64" width="12" height="165.5" />
      <rect
        fill="#3F3834"
        x="223.5"
        y="368.14"
        transform="matrix(0.8663 -0.4995 0.4995 0.8663 -148.4124 187.7563)"
        width="106.13"
        height="6"
      />
      <rect
        fill="#3F3834"
        x="365.51"
        y="318.08"
        transform="matrix(0.4994 -0.8664 0.8664 0.4994 -137.0787 505.0417)"
        width="6"
        height="106.13"
      />
      <rect fill="#3F3834" x="319.54" y="238.64" width="6" height="106.01" />
    </svg>
  );
}
